<template>
    <div class="dst dk-range-slider" :class="{ 'has--min-fixed': minFixed, 'has--max-fixed': maxFixed }">
        <!-- @slot Custom content above the slider -->
        <slot name="above" v-bind="slider"></slot>

        <div
            :class="{
                [`js-nouislider--uid_${_uid}`]: true,
            }"
        ></div>

        <div v-if="canEdit && from && to" class="tw-flex tw-justify-between tw-items-center tw-mt-8">
            <div class="tw-flex tw-items-center tw-max-w-[45%]">
                <span class="tw-mr-2">{{ fromText }}</span>
                <div
                    class="tw-flex tw-items-center tw-flex-shrink tw-border tw-border-solid tw-border-brand-secondary tw-p-3 tw-bg-white"
                >
                    <label v-if="maskedFromPrefix">{{ maskedFromPrefix }}&nbsp;</label>
                    <input
                        class="!tw-p-0 !tw-border-0 tw-bg-transparent tw-flex-0 tw-w-full tw-flex-1"
                        :class="{ 'tw-text-right': maskedFromPostfix }"
                        :value="maskedFrom"
                        type="text"
                        :readonly="isFromReadOnly ? 'readonly' : null"
                        @input="setMaskedFromValue($event.target.value)"
                        @keyup.enter="setSliderValue($event.target.value, 0)"
                        @blur="setSliderValue($event.target.value, 0)"
                        @focus="setSelection"
                        @keypress="onKeyPress"
                    >
                    <label v-if="maskedFromPostfix">&nbsp;{{ maskedFromPostfix }}</label>
                </div>
            </div>
            <div class="tw-flex tw-items-center tw-max-w-[45%]">
                <span :class="{ 'tw-mr-2': !shouldShowMaxText }">{{ toText }}</span>
                <div
                    v-if="!shouldShowMaxText"
                    class="tw-flex tw-items-center tw-flex-shrink tw-border tw-border-solid tw-border-brand-secondary tw-p-3 tw-bg-white"
                >
                    <label v-if="maskedToPrefix">{{ maskedToPrefix }}&nbsp;</label>
                    <input
                        class="!tw-p-0 !tw-border-0 tw-bg-transparent tw-flex-0 tw-w-full tw-flex-1"
                        :class="{ 'tw-text-right': maskedToPostfix }"
                        :value="maskedTo"
                        type="text"
                        :readonly="isToReadOnly ? 'readonly' : null"
                        @input="setMaskedToValue($event.target.value)"
                        @keyup.enter="setSliderValue($event.target.value, 1)"
                        @blur="setSliderValue($event.target.value, 1)"
                        @focus="setSelection"
                        @keypress="onKeyPress"
                    >
                    <label v-if="maskedToPostfix">&nbsp;{{ maskedToPostfix }}</label>
                </div>
            </div>
        </div>

        <!-- @slot Custom content below the slider -->
        <slot v-bind="slider">
            <div class="tw-mt-8">
                <div v-if="!canEdit && from && to" class="tw-flex tw-justify-between tw-items-center">
                    <span>
                        {{ fromText }}
                    </span>
                    <span>
                        {{ toText }}
                    </span>
                </div>
            </div>
        </slot>
    </div>
</template>

<script>
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import { hasSignPrefix, getCurrencySign } from '@v4/common/utils/currency';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';

export const rangeFormatEnum = Object.freeze({
    DEFAULT: 'default',
    PRICE: 'price',
    DIMENSION: 'dimension',
    CO2: 'co2',
    WLTP_EMISSIONS_COMBINED: 'wltpEmissionsCombined',
    HP: 'hp',
    LITERS: 'liters',
    CONSUMPTION: 'consumption',
    ELECTRIC_RANGE: 'electric-range',
    BATTERY_CAPACITY: 'battery-capacity',
    ENGINE_POWER: 'engine-power',
});

/**
 * A range slider component which is wrapping noUiSlider.
 *
 * @version 4.0.0
 * @see [noUiSlider](https://refreshless.com/nouislider/)
 * @example ./../examples/DkRangeSlider.md
 */
export default {
    name: 'DkRangeSlider',
    props: {
        /**
         * @see [noUiSlider options](https://refreshless.com/nouislider/slider-options/)
         */
        nouisliderOpts: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Available formats: price, dimension, co2, hp, liters, electric-range, battery-capacity, engine-power
         */
        format: {
            type: String,
            default: rangeFormatEnum.DEFAULT,
        },
        /**
         * Whether to show inputs to edit minimum and maximum value. When `true` listen to `@set` slider event.
         *
         * @see [noUiSlider events](https://refreshless.com/nouislider/events-callbacks/)
         */
        editable: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether the `from` input is read-only
         */
        isFromReadOnly: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether the `to` input is read-only
         */
        isToReadOnly: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether the `min` value is fixed
         */
        minFixed: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether the `max` value is fixed
         */
        maxFixed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            noUiSlider: null,
            slider: {},
            sliderFormat: {},
            maskedFrom: null,
            maskedTo: null,
            maskedFromPrefix: null,
            maskedFromPostfix: null,
            maskedToPrefix: null,
            maskedToPostfix: null,
            showMaxPriceValue: true,
        };
    },
    computed: {
        canEdit() {
            return (
                this.editable &&
                ![rangeFormatEnum.DIMENSION, rangeFormatEnum.LITERS, rangeFormatEnum.CONSUMPTION].includes(this.format)
            );
        },
        nouisliderOptions() {
            this.formatSliderOutput();

            return {
                connect: true,
                format: this.sliderFormat.values,
                ...this.nouisliderOpts,
            };
        },
        from() {
            return (this.slider && this.slider.values && this.slider.values[0]) || '';
        },
        to() {
            return (this.slider && this.slider.values && this.slider.values[1]) || '';
        },
        shouldShowMaxText() {
            if (this.showMaxPriceValue) {
                return false;
            }

            return (
                this.noUiSlider &&
                this.noUiSlider.options &&
                this.slider.unencoded &&
                this.noUiSlider.options.range.max.toString() === this.slider.unencoded[1] &&
                this.format === rangeFormatEnum.PRICE
            );
        },
        fromText() {
            return `${this.$t('dk-range-slider.from').toLowerCase()}${this.canEdit ? '' : ` ${this.from}`}`;
        },
        toText() {
            if (this.shouldShowMaxText) {
                return this.$t('dk-range-slider.max');
            }

            return `${this.$t('dk-range-slider.to').toLowerCase()}${this.canEdit ? '' : ` ${this.to}`}`;
        },
    },
    watch: {
        nouisliderOptions: {
            deep: true,
            handler(newOptions, oldOptions) {
                if (isEqual(newOptions, oldOptions)) {
                    return;
                }

                this.$nextTick(() => this.updateOptions(newOptions));
            },
        },
    },
    mounted() {
        this.createNoUiSlider();
    },
    destroyed() {
        if (!this.noUiSlider) {
            return;
        }

        this.noUiSlider.off();
    },
    methods: {
        createNoUiSlider() {
            this.noUiSlider = noUiSlider.create(
                document.querySelector(`.js-nouislider--uid_${this._uid}`),
                this.nouisliderOptions
            );
            this.bindEvents();
        },
        /**
         * Emits all noUiSlider events.
         *
         * @see {@link https://refreshless.com/nouislider/events-callbacks/}
         * @see {@link https://refreshless.com/nouislider/reference/}
         * @see {@link https://refreshless.com/nouislider/reference/#section-event-callback}
         *
         * @return {void}
         */
        bindEvents() {
            if (!this.noUiSlider) {
                return;
            }

            for (const eventName of ['update', 'change', 'set', 'slide', 'start', 'end']) {
                this.noUiSlider.on(eventName, (...args) => {
                    // Formatting unencoded values
                    args[2][0] = this.sliderFormat.unencoded.to(args[2][0]);
                    args[2][1] = this.sliderFormat.unencoded.to(args[2][1]);

                    this.$emit(eventName, args, this.$el);

                    if (eventName === 'update') {
                        const [values, handle, unencoded, tap, positions] = args;

                        this.$set(this, 'slider', {
                            values,
                            handle,
                            unencoded,
                            tap,
                            positions,
                        });

                        this.$nextTick(() => {
                            this.$set(this.slider, 'fromText', this.fromText);
                            this.$set(this.slider, 'toText', this.toText);

                            this.setMaskedFromValue(args[2][0]);
                            this.setMaskedToValue(args[2][1]);
                        });
                    }
                });
            }
        },
        /**
         * Sets slider formats
         *
         * @see {@link https://refreshless.com/wnumb/options/|Options}
         * @param {object} options - wNumb options
         * @param {number} [options.decimals=0] - The number of decimals to include in the result. Limited to 7.
         */
        setSliderFormats(options = { decimals: 0 }) {
            this.$set(this.sliderFormat, 'values', wNumb(options));
            this.$set(this.sliderFormat, 'unencoded', wNumb({ decimals: options.decimals }));
        },
        getFormatConfiguration(format) {
            let config;
            switch (format) {
                case rangeFormatEnum.PRICE: {
                    const hasPrefix = hasSignPrefix();
                    const thousandsSeparator = { en: ',', fr: ' ' };

                    config = {
                        decimals: 0,
                        prefix: hasPrefix ? `${getCurrencySign(this.$i18n.locale)} ` : '',
                        postfix: hasPrefix ? '' : ` ${getCurrencySign(this.$i18n.locale)}`,
                        thousand: thousandsSeparator[this.$i18n.locale] || '.',
                    };

                    break;
                }
                case rangeFormatEnum.DIMENSION:
                    config = {
                        decimals: 0,
                        postfix: ' cm',
                        encoder: (value) => value / 10,
                    };

                    break;
                case rangeFormatEnum.CO2:
                case rangeFormatEnum.WLTP_EMISSIONS_COMBINED:
                    config = { decimals: 0, postfix: ' g/km' };

                    break;
                case rangeFormatEnum.HP:
                    config = { decimals: 0, postfix: ` ${this.$t('dk-range-slider.hp').toUpperCase()}` };

                    break;
                case rangeFormatEnum.LITERS:
                    config = { decimals: 1, postfix: ' l' };

                    break;
                case rangeFormatEnum.CONSUMPTION:
                    config = { decimals: 1, postfix: ` ${this.$t('dk-range-slider.consumption')}` };

                    break;
                case rangeFormatEnum.ELECTRIC_RANGE:
                    config = { decimals: 0, postfix: ` ${this.$t('dk-range-slider.km')}` };

                    break;
                case rangeFormatEnum.BATTERY_CAPACITY:
                    config = { decimals: 1, postfix: ' KWh' };

                    break;
                case rangeFormatEnum.ENGINE_POWER:
                    config = { decimals: 0, postfix: ' KW' };

                    break;
                case rangeFormatEnum.DEFAULT:
                default:
                    config = {};

                    break;
            }

            return config;
        },
        formatSliderOutput() {
            this.setSliderFormats(this.getFormatConfiguration(this.format));
        },
        /**
         * Update noUiSlider options
         *
         * @param {object} options - new noUiSlider options
         * @param {boolean} fireSetEvent
         * @see {@link https://refreshless.com/nouislider/more/#section-update|Updating and reading slider options}
         * @public
         */
        updateOptions(options = {}, fireSetEvent = false) {
            if (!this.noUiSlider || !isFunction(this.noUiSlider.updateOptions)) {
                return;
            }

            this.noUiSlider.updateOptions(options, fireSetEvent);
        },
        setSliderValue(value, handle) {
            const currentValue = this.sliderFormat.values.from(this.slider.values[handle]);

            // Set a new value to the slider only when different from the current one
            if (this.sliderFormat.values.from(value) === currentValue) {
                return;
            }

            const values = [null, null];
            values[handle] = value;

            this.noUiSlider.set(values);
        },
        setSelection(e) {
            e.target.select();
        },
        getMaskedData(value) {
            let config = this.getFormatConfiguration(this.format);
            const { prefix, postfix } = config;

            config = {
                ...config,
                postfix: undefined,
                prefix: undefined,
                suffix: undefined,
            };

            const { from, to } = wNumb(config);
            const formattedValue = to && from ? to(from(value)) : value;

            return {
                value: formattedValue !== false ? formattedValue : '',
                prefix,
                postfix,
            };
        },
        setMaskedFromValue(value) {
            const { value: maskedValue, prefix, postfix } = this.getMaskedData(value);

            this.maskedFrom = maskedValue;
            this.maskedFromPrefix = prefix;
            this.maskedFromPostfix = postfix;
        },
        setMaskedToValue(value) {
            const { value: maskedValue, prefix, postfix } = this.getMaskedData(value);

            this.maskedTo = maskedValue;
            this.maskedToPrefix = prefix;
            this.maskedToPostfix = postfix;
        },
        onKeyPress(event) {
            if (isNaN(event.key)) {
                event.preventDefault();
            }
        },
    },
};
</script>

<style>
@import '~nouislider/distribute/nouislider.css';

.dk-range-slider.has--min-fixed .noUi-base .noUi-connects + .noUi-origin {
    @apply tw-hidden;
}

.dk-range-slider.has--max-fixed .noUi-base .noUi-origin:last-of-type {
    @apply tw-hidden;
}
</style>
